
import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6jvg23cs59vsrrrk8qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section id="contact" className="py-32 bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div>
            <div className="mb-12">
              <h2 className="text-6xl lg:text-7xl font-black text-white mb-4">
                JIM d.o.o.
              </h2>
              <div className="text-5xl lg:text-6xl font-normal text-gray-300">
                Mostar
              </div>
              <div className="mt-6 h-0.5 w-32 bg-white"></div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-map-pin-line text-gray-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-xs uppercase tracking-wider text-white font-semibold">Address</span>
                </div>
                <p className="text-base text-gray-300 ml-9">
                  Blajburskih zrtava bb<br />
                  Mostar, 88000<br />
                  Bosnia &amp; Herzegovina
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-mail-line text-gray-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-xs uppercase tracking-wider text-white font-semibold">Email</span>
                </div>
                <a 
                  href="mailto:info@jimdoomostar.com" 
                  className="text-base text-gray-300 ml-9 hover:text-teal-400 transition-colors"
                >
                  info@jimdoomostar.com
                </a>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-phone-line text-gray-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-xs uppercase tracking-wider text-white font-semibold">Phone</span>
                </div>
                <a 
                  href="tel:+13186977996" 
                  className="text-base text-gray-300 ml-9 hover:text-teal-400 transition-colors"
                >
                  +1 3186977996
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-10">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Get In Touch</h3>
            <p className="text-gray-600 mb-8">
              Have questions about our products? We'd love to hear from you.
            </p>

            <form id="contact_form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Your message..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitStatus === 'submitting'}
                  className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {submitStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                    Sorry, there was an error sending your message. Please try again.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-20 pt-12 border-t border-gray-700">
          <div className="text-center text-sm text-gray-400">
            © 2025 JIM d.o.o. Mostar. All Rights Reserved.
          </div>
        </div>
      </div>
    </section>
  );
}